import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { findApp, SUPPORT_EMAIL } from '../data/apps';
import { AppIcon } from './Apps';
import './Apps.css';

const Section = ({ s }) => (
  <section className="policy-section">
    <h2>{s.heading}</h2>
    {(s.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
    {s.bullets && (
      <ul>
        {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    )}
  </section>
);

const TITLES = { privacy: 'Privacy Policy', support: 'Support', terms: 'Terms of Use' };

// kind: 'privacy' | 'support' | 'terms'
const AppPolicy = ({ kind }) => {
  const { slug } = useParams();
  const app = findApp(slug);
  const doc = app && app[kind];

  const title = TITLES[kind];

  useEffect(() => {
    if (app) document.title = `${title} — ${app.name}`;
    window.scrollTo(0, 0);
  }, [app, title]);

  if (!app || !doc) return <Navigate to={app ? `/apps/${app.slug}` : '/apps'} replace />;

  return (
    <div className="apps-page policy-page">
      <section className="policy-hero">
        <div className="container narrow">
          <p className="apps-eyebrow">
            <Link to="/apps">Apps</Link> / <Link to={`/apps/${app.slug}`}>{app.name}</Link>
          </p>
          <header className="policy-header">
            <AppIcon app={app} size={56} />
            <div>
              <h1>{title}</h1>
              {doc.effective && <p className="policy-effective">Effective {doc.effective}</p>}
            </div>
          </header>
        </div>
      </section>
      <div className="container narrow policy-body">
        {doc.intro && <p className="policy-intro">{doc.intro}</p>}

        {kind === 'support' ? (
          <>
            <section className="policy-section">
              <h2>Frequently asked questions</h2>
              {doc.faq.map((item) => (
                <details className="faq-item" key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </section>
            <section className="policy-section">
              <h2>Still stuck?</h2>
              <p>
                Email <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(app.name)}`}>{SUPPORT_EMAIL}</a> with
                the app name, your device and iOS version, and what you expected to happen. A real person answers.
              </p>
              <p>
                See also the <Link to={`/apps/${app.slug}/privacy`}>privacy policy</Link> and{' '}
                <Link to={`/apps/${app.slug}/terms`}>terms of use</Link>.
              </p>
            </section>
          </>
        ) : (
          doc.sections.map((s) => <Section s={s} key={s.heading} />)
        )}

        <p className="policy-footer">
          {app.name} is published by SVT Consulting LLC. Our website's own privacy policy is at{' '}
          <Link to="/privacy">svtconsulting.co/privacy</Link>.
        </p>
      </div>
    </div>
  );
};

export default AppPolicy;
